/* global gdAlertify */
import React, { useState, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import axiosInstance from "../../config/axiosConfig";
import { calculateAge, formatDate } from "../../utils/utils";

const clasificacionMap = {
    0: '',
    1: 'Básico 0',
    2: 'Básico 0-1',
    3: 'Básico 1',
    4: 'Básico 1-2',
    5: 'Básico 2',
    6: 'Básico 2-3',
    7: 'Básico 3',
    8: 'D',
};

const sexoMap = { 1: 'Masculino', 2: 'Femenino', 3: 'Otro' };

const anticipoMap = { 0: 'No', 1: 'Si' };

const tipoDocumentoMap = {
    RUT: 'RUT',
    rutProvisorio: 'RUT provisorio',
    Pasaporte: 'Pasaporte',
    rutExtranjero: 'RUT extranjero',
};

// Grupos y campos tal como aparecen en el formulario de edición del auxiliar
// (IngresoAuxiliar.js). "Contraseña SII" queda fuera a propósito: es una
// contraseña en texto plano y no debe terminar en un Excel que circula por mail.
const buildGrupos = (especialidadNombreMap, turnoNombreMap) => [
    {
        id: "personales",
        label: "Datos Personales",
        campos: [
            { key: "rut", label: "RUT o Pasaporte", getValue: (a) => a.rut },
            { key: "tipoDocumento", label: "Tipo de Documento", getValue: (a) => tipoDocumentoMap[a.tipoDocumento] || a.tipoDocumento },
            { key: "nombre", label: "Nombre", getValue: (a) => a.nombre },
            { key: "primerApellido", label: "Primer Apellido", getValue: (a) => a.apellidoPaterno },
            { key: "segundoApellido", label: "Segundo Apellido", getValue: (a) => a.apellidoMaterno },
            { key: "numeroCelular", label: "Número de Celular", getValue: (a) => a.celular },
            { key: "estatura", label: "Estatura (cm)", getValue: (a) => a.estatura },
            { key: "nacionalidad", label: "Nacionalidad", getValue: (a) => a.nacionalidad },
            { key: "situacionMigratoria", label: "Situación migratoria", getValue: (a) => a.situacionMigratoria },
            { key: "email", label: "Email", getValue: (a) => a.email },
            { key: "direccion", label: "Dirección", getValue: (a) => a.direccion },
            { key: "comuna", label: "Comuna", getValue: (a) => a.des_comuna },
            { key: "fechaNacimiento", label: "Fecha de Nacimiento", getValue: (a) => formatDate(a.fechaNacimiento) },
            { key: "edad", label: "Edad", getValue: (a) => calculateAge(a.fechaNacimiento) },
            { key: "sexo", label: "Género", getValue: (a) => sexoMap[a.sexo] || "" },
            { key: "fechaIngreso", label: "Fecha Ingreso", getValue: (a) => formatDate(a.fechaIngreso) },
            { key: "fechaRetiro", label: "Fecha Retiro", getValue: (a) => formatDate(a.fechaRetiro) },
            { key: "fechaReintegro", label: "Fecha Reintegro", getValue: (a) => formatDate(a.fechaReintegro) },
            { key: "presentacionPersonal", label: "Presentación Personal", getValue: (a) => a.presentacionPersonal },
            { key: "observacionesPersonales", label: "Observaciones Iniciales", getValue: (a) => a.observaciones },
        ],
    },
    {
        id: "profesionales",
        label: "Datos Profesionales",
        campos: [
            { key: "titulo", label: "Título o Diploma", getValue: (a) => a.titulo },
            { key: "institucion", label: "Institución", getValue: (a) => a.institucion },
            {
                key: "especialidades",
                label: "Especialidades",
                getValue: (a) => (a.especialidades || [])
                    .map((e) => especialidadNombreMap[e.especialidadID])
                    .filter(Boolean)
                    .join(", "),
            },
            { key: "categoria", label: "Categoría", getValue: (a) => a.des_categoria },
            { key: "obsCategoria", label: "Observaciones Categoría", getValue: (a) => a.obs_categoria },
            { key: "clasificacion", label: "Clasificación", getValue: (a) => clasificacionMap[a.clasificacionID] || "" },
            { key: "anticipo", label: "Anticipo", getValue: (a) => anticipoMap[a.anticipo] ?? "" },
            { key: "evaluacion", label: "Evaluación", getValue: (a) => a.evaluacion },
            { key: "experienciaDomicilio", label: "Experiencia en domicilio", getValue: (a) => a.experienciaDomicilio },
            { key: "experienciaGeriatica", label: "Experiencia Institución Geriátrica", getValue: (a) => a.experienciaGeriatica },
            { key: "recomendadaPor", label: "Recomendada por", getValue: (a) => a.recomendadaPor },
        ],
    },
    {
        id: "turnos",
        label: "Disponibilidad de Turnos",
        campos: [
            {
                key: "turnosDisponibles",
                label: "Turnos disponibles",
                getValue: (a) => (a.turnos || [])
                    .map((t) => turnoNombreMap[t.turnoID])
                    .filter(Boolean)
                    .join(", "),
            },
            { key: "obsTurnos", label: "Observaciones Turnos", getValue: (a) => a.obsTurnos },
        ],
    },
    {
        id: "pago",
        label: "Datos de Pago",
        campos: [
            { key: "tipoCuenta", label: "Tipo de Cuenta", getValue: (a) => a.datosPago?.tipo_cuenta },
            { key: "rutPago", label: "RUT Pago", getValue: (a) => a.datosPago?.rut_pago },
            { key: "nombrePago", label: "Nombre Pago", getValue: (a) => a.datosPago?.nombre_pago },
            { key: "banco", label: "Banco", getValue: (a) => a.datosPago?.banco },
            { key: "tipoCuentaBancaria", label: "Tipo de Cuenta Bancaria", getValue: (a) => a.datosPago?.tipo_cuenta_bancaria },
            { key: "numeroCuenta", label: "Número de Cuenta", getValue: (a) => a.datosPago?.numero_cuenta },
            { key: "tipoBoleta", label: "Tipo Boleta", getValue: (a) => a.datosPago?.tipo_boleta },
            { key: "rutBoleta", label: "RUT Boleta", getValue: (a) => a.datosPago?.rut_boleta },
            { key: "nombreBoleta", label: "Nombre Boleta", getValue: (a) => a.datosPago?.nombre_boleta },
            { key: "datosSII", label: "Datos SII", getValue: (a) => a.datosPago?.datos_sii },
            { key: "contrasenaSII", label: "Contraseña SII", getValue: (a) => a.datosPago?.contrasena_sii },
        ],
    },
];

const ExportarAuxiliaresModal = ({ isOpen, onClose, totalAuxiliares = 0, filtroParams = { cordinacion: true } }) => {
    const grupos = useMemo(() => buildGrupos({}, {}), []);
    const todosLosCampos = useMemo(() => grupos.flatMap((g) => g.campos.map((c) => c.key)), [grupos]);

    const [selectedFields, setSelectedFields] = useState(new Set());
    const [exportando, setExportando] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setSelectedFields(new Set(todosLosCampos));
        }
    }, [isOpen, todosLosCampos]);

    if (!isOpen) return null;

    const toggleCampo = (key) => {
        setSelectedFields((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const toggleGrupo = (grupo, marcar) => {
        setSelectedFields((prev) => {
            const next = new Set(prev);
            grupo.campos.forEach((c) => {
                if (marcar) {
                    next.add(c.key);
                } else {
                    next.delete(c.key);
                }
            });
            return next;
        });
    };

    const seleccionarTodos = () => setSelectedFields(new Set(todosLosCampos));
    const deseleccionarTodos = () => setSelectedFields(new Set());

    const handleExportar = async () => {
        if (selectedFields.size === 0) {
            gdAlertify.notiError("Debe seleccionar al menos un dato del formulario.");
            return;
        }

        setExportando(true);
        try {
            const [detalleResponse, especialidadesResponse, turnosResponse] = await Promise.all([
                axiosInstance.get("/auxiliar/busqueda", { params: { ...filtroParams, detalle_extendido: 1 } }),
                axiosInstance.get("/params/especialidades"),
                axiosInstance.get("/params/turnos"),
            ]);

            const especialidadNombreMap = {};
            (especialidadesResponse.data.especialidades || []).forEach((esp) => {
                especialidadNombreMap[esp.idEspecialidad] = esp.nombreEspecialidad;
            });

            const turnoNombreMap = {};
            (turnosResponse.data.turnos || []).forEach((turno) => {
                turnoNombreMap[turno.id] = turno.nombre;
            });

            const gruposConMapas = buildGrupos(especialidadNombreMap, turnoNombreMap);
            const camposActivos = gruposConMapas
                .flatMap((g) => g.campos)
                .filter((c) => selectedFields.has(c.key));

            const auxiliaresData = detalleResponse.data.data || [];

            const encabezado = camposActivos.map((c) => c.label);
            const filas = auxiliaresData.map((aux) => camposActivos.map((c) => c.getValue(aux) ?? ""));

            const worksheet = XLSX.utils.aoa_to_sheet([encabezado, ...filas]);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Auxiliares");
            XLSX.writeFile(workbook, `nomina-auxiliares-ficha-completa-${Date.now()}.xlsx`);

            gdAlertify.notiSuccess(`Se exportaron ${filas.length} auxiliares.`);
            onClose();
        } catch (error) {
            console.error("Error al exportar auxiliares:", error);
            gdAlertify.notiError("Ocurrió un error al exportar los datos.");
        } finally {
            setExportando(false);
        }
    };

    return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Exportar Datos - Auxiliares</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <p className="text-muted small mb-2">
                            Seleccione los datos del formulario que quiere incluir en el Excel. Se exportará una fila por
                            cada uno de los {totalAuxiliares || ""} auxiliares, con las columnas marcadas.
                        </p>
                        <div className="d-flex gap-2 mb-3">
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={seleccionarTodos}>
                                Seleccionar todos
                            </button>
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={deseleccionarTodos}>
                                Ninguno
                            </button>
                        </div>

                        {grupos.map((grupo) => {
                            const marcados = grupo.campos.filter((c) => selectedFields.has(c.key)).length;
                            const todosMarcados = marcados === grupo.campos.length;
                            return (
                                <div className="border rounded mb-3" key={grupo.id}>
                                    <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-light border-bottom">
                                        <div className="form-check mb-0">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id={`grupo-${grupo.id}`}
                                                checked={todosMarcados}
                                                onChange={(e) => toggleGrupo(grupo, e.target.checked)}
                                            />
                                            <label className="form-check-label fw-semibold" htmlFor={`grupo-${grupo.id}`}>
                                                {grupo.label}
                                            </label>
                                        </div>
                                        <span className="text-muted small">{marcados}/{grupo.campos.length}</span>
                                    </div>
                                    <div className="row px-3 py-2 g-2">
                                        {grupo.campos.map((campo) => (
                                            <div className="col-md-6" key={campo.key}>
                                                <div className="form-check">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id={`campo-${campo.key}`}
                                                        checked={selectedFields.has(campo.key)}
                                                        onChange={() => toggleCampo(campo.key)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`campo-${campo.key}`}>
                                                        {campo.label}
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="modal-footer d-flex justify-content-between align-items-center">
                        <span className="text-muted small">{selectedFields.size} dato(s) seleccionado(s)</span>
                        <div>
                            <button type="button" className="btn btn-secondary me-2" onClick={onClose} disabled={exportando}>
                                Cancelar
                            </button>
                            <button type="button" className="btn btn-success" onClick={handleExportar} disabled={exportando}>
                                {exportando ? "Exportando..." : "Exportar"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExportarAuxiliaresModal;
